import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import styles from "./FolderDeleteModal.module.css";
import { getMessageFromNetworkError, useDeleteFolder } from "@zapier/client";
import { useNotification } from "@zapier/utils";
import { useRouter, useParams } from "next/navigation";

interface Props {
  folderName?: string;
  id?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface ContentProps extends Props {
  onSubmit: () => void;
}

function FolderDeleteModalContent({
  folderName,
  onSubmit,
  setIsOpen,
}: ContentProps) {
  const t = useTranslations("folder-delete-modal");

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={onSubmit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            return event.key === "Enter" && onSubmit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.delete-folder-title", { folderName: folderName || "" })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <div className={styles.body}>
            {t("messages.confirm-delete", { folderName: folderName || "" })}
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button variant="danger" type="submit" onPress={onSubmit}>
            {t("buttons.delete-folder")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function FolderDeleteModal({
  folderName = "",
  setIsOpen,
  id,
  isOpen,
}: Props) {
  const t = useTranslations("folder-delete-modal");

  const { createNotification } = useNotification();
  const { delete: handleDeleteFolder } = useDeleteFolder();
  const parameters = useParams();
  const { push } = useRouter();

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = () => {
    if (!id) {
      return;
    }

    handleDeleteFolder(
      { folderId: id },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.folder-deleted-success", {
                  folderName: folderName || "",
                })}
              </Text>
            ),
            type: "success",
          });

          // If user is on the folder details page, return to the folder list
          // If the user is on the folder list, do not redirect
          if (parameters?.folder) {
            push("/app/assets/zaps/folders");
          }
        },
        onError: (error: any) => {
          createNotification({
            title: `Unable to delete "${folderName}"`,
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <FolderDeleteModalContent
      folderName={folderName}
      id={id}
      isOpen={isOpen}
      onSubmit={submit}
      setIsOpen={setIsOpen}
    />
  );
}
